import jwt_decode from 'jwt-decode';
import { createContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import SplashScreen from '../components/SplashScreen';
import useLocalStorage from '../hooks/useLocalStorage';
import instance from '../utils/imsAxios';
import { resetStore } from '../store';

const authContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [auth, isInitialised] = useProvideAuth();
    return isInitialised ? <authContext.Provider value={auth}>{children}</authContext.Provider> : <SplashScreen />;
};

const useProvideAuth = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { pathname = '/', state } = useLocation();
    const [storedToken, setStoredToken] = useLocalStorage('token', null);
    const [storedUser, setStoredUser] = useLocalStorage('user', null);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [viewable, setViewable] = useState({ modules: [] });
    const [editable, setEditable] = useState({ modules: [] });
    const [deletable, setDeletable] = useState({ modules: [] });
    const [isInitialised, setIsInitialised] = useState(false);

    const setupUserPermissions = (user) => {
        const { userPermissions = [] } = user || {};
        const normalizedRole = (user?.roleDescription || '').toLowerCase();
        let viewableModules = [];
        let editableModules = [];
        let deletableModules = [];
        userPermissions.forEach((module) => {
            if(module.view) {
                viewableModules.push(module.module)
            }
            if(module.edit) {
                editableModules.push(module.module)
            }
            if(module.del) {
                deletableModules.push(module.module)
            }
        });
        if (!userPermissions.some((permission) => permission.module === 'job-card-pricing') && normalizedRole !== 'clerk') {
            viewableModules.push('job-card-pricing');
        }
        if (!userPermissions.some((permission) => permission.module === 'job-card-settings-minimum-charge') && (normalizedRole === 'administrator' || normalizedRole === 'admin')) {
            editableModules.push('job-card-settings-minimum-charge');
        }
        if (!userPermissions.some((permission) => permission.module === 'job-card-settings-gold-price') && (normalizedRole === 'administrator' || normalizedRole === 'admin' || normalizedRole === 'manager')) {
            editableModules.push('job-card-settings-gold-price');
        }
        if (!userPermissions.some((permission) => permission.module === 'job-card-settings-road-rates') && (normalizedRole === 'administrator' || normalizedRole === 'admin' || normalizedRole === 'manager')) {
            editableModules.push('job-card-settings-road-rates');
        }
        // Same default as the API: administrators and managers open job card settings
        if (!userPermissions.some((permission) => permission.module === 'job-card-settings') && (normalizedRole === 'administrator' || normalizedRole === 'admin' || normalizedRole === 'manager')) {
            viewableModules.push('job-card-settings');
            editableModules.push('job-card-settings');
        }
        // Same default as the API: administrators and managers count stock and see the dashboard
        const managerOrAbove = normalizedRole === 'administrator' || normalizedRole === 'admin' || normalizedRole === 'manager';
        if (!userPermissions.some((permission) => permission.module === 'stock-take') && managerOrAbove) {
            viewableModules.push('stock-take');
            editableModules.push('stock-take');
        }
        if (!userPermissions.some((permission) => permission.module === 'dashboard') && managerOrAbove) {
            viewableModules.push('dashboard');
        }
        // Same default as the API: administrators see the Sync page
        if (!userPermissions.some((permission) => permission.module === 'sync') && (normalizedRole === 'administrator' || normalizedRole === 'admin')) {
            viewableModules.push('sync');
            editableModules.push('sync');
        }
        // Same default as the API: administrators install app updates
        if (!userPermissions.some((permission) => permission.module === 'system-update') && (normalizedRole === 'administrator' || normalizedRole === 'admin')) {
            viewableModules.push('system-update');
            editableModules.push('system-update');
        }
        if(user._id) viewableModules.push('select-site')
        setViewable([...new Set(viewableModules)]);
        setEditable([...new Set(editableModules)]);
        setDeletable([...new Set(deletableModules)]);
    };

    const setSession = (authToken, user, inactivity) => {
        if (authToken && user) {
            instance.defaults.headers.common.Authorization = authToken;
            setStoredToken(authToken);
            setStoredUser(user);
        } else {
            delete instance.defaults.headers.common.Authorization;
            setStoredToken(null);
            setStoredUser(null);
            if(!inactivity) localStorage.setItem('selectedSite', null)
        }
    };

    const login = async ({ userName, password, location } = {}) => {
        let from = (localStorage.getItem('selectedSite')?.siteId && state) ? state.from?.pathname : '/ims/select-site';
        
        if (!isValidToken(storedToken)) {
            setSession(null, null, !!location);
        }
        if (!location) dispatch(resetStore());
        try {
            const {
                data: { user, token }
            } = await instance.post('/login', { userName, password });
            setSession(token, user);
            setUser(user);
            setToken(token);
            setupUserPermissions(user);
            return navigate(location ?? from, { replace: true });
        } catch (error) {
            if(!location) {
                setUser(null);
                setToken(null);
                setSession();
            }
            // Show the server's reason, such as a login locked after too many wrong passwords
            const reason = error.response?.data;
            throw new Error(typeof reason === 'string' && reason ? reason : 'Invalid Username / Password!');
        }
    };

    const logout = () => {
        setSession();
        setUser(null);
        setToken(null);
        dispatch(resetStore());
        return navigate('/ims/login');
    };

    // The API answers 401 when the login itself is no longer valid (user deleted, session over),
    // so that ends the session here too. A missing permission is a 403 and does not.
    useEffect(() => {
        const interceptor = instance.interceptors.response.use(undefined, (error) => {
            if (error.response?.status === 401 && instance.defaults.headers.common.Authorization) logout();
            return Promise.reject(error);
        });
        return () => instance.interceptors.response.eject(interceptor);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canView = (module) => {
        // if (user.roleDescription === 'Administrator') return true;
        // if (user.roleDescription === 'Standard') return true;
        return viewable?.includes(module);
    };

    const canEdit = (module) => {
        // if (user.roleDescription === 'Administrator') return true;
        return editable?.includes(module);
    };

    const canDelete = (module) => {
        // if (user.roleDescription === 'Administrator') return true;
        return deletable?.includes(module);
    };

    const isValidToken = (accessToken) => {
        if (!accessToken) {
            return false;
        }

        const decoded = jwt_decode(accessToken);
        const currentTime = Date.now() / 1000;
        const res = decoded.exp > currentTime;
        return res;
    };

    const refreshToken = async () => {
        const {
            data: { user, token }
        } = await instance.post('/login');
        // set new session with new token and user
        setSession(token, user);
        setUser(user);
        setToken(token);
        // finish setting up User and Permissions
        setupUserPermissions(user);
    };

    // Renews the token while the user is active; the API refuses a token 16 minutes after its last renewal
    const keepAlive = async () => {
        const sent = instance.defaults.headers.common.Authorization;
        // Locked by the idle prompt or signed out: there is nothing to renew
        if (!sent) return;
        try {
            const { data: { token } } = await instance.post('/login');
            // Signed out, or someone else signed in, while this was on its way: leave their session alone
            if (instance.defaults.headers.common.Authorization !== sent) return;
            instance.defaults.headers.common.Authorization = token;
            setStoredToken(token);
            setToken(token);
        } catch (error) {
            // The session is over (idle too long, or the 12-hour limit): sign out. If the API could not be reached, keep going
            if (error.response && instance.defaults.headers.common.Authorization === sent) logout();
        }
    };

    useEffect(() => {
        // initialise authentication to check if valid token stored in local storage, redirect if its not
        (async () => {
            try {
                if (storedToken) {
                    // if there is a local token, set the session to attach it to the req headers
                    setSession(storedToken, storedUser);
                    const tokenIsValid = isValidToken(storedToken);
                    // send a login request to the backend to check the token is valid. If valid return a new token
                    // with extended expiration
                    if (tokenIsValid) {
                        setUser(storedUser);
                        setToken(storedToken);
                        setupUserPermissions(storedUser);
                        try {
                            await refreshToken();
                        } catch (error) {
                            // The API refused the login (user deleted, or the session is over): log out.
                            // If the API could not be reached, keep the stored login.
                            if (error.response) throw error;
                        }
                    } else {
                        setUser(null);
                        setToken(null);
                        setSession();
                    }
                    setIsInitialised(true);
                    // if path is /login then reroute to /dashboard
                    if (pathname === '/ims/login') {
                        return navigate('/ims/operations/job-cards', { replace: true });
                    }
                } 
                else {
                    setUser(null);
                    setToken(null);
                    setSession();
                    setIsInitialised(true);
                    return navigate('/ims/login');
                }
            } catch (error) {
                setUser(null);
                setToken(null);
                setSession();
                setIsInitialised(true);
                return navigate('/ims/login');
            }
        })();
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [
        {
            user,
            token,
            companyId: user?.companyId,
            login,
            logout,
            canView,
            canEdit,
            canDelete,
            setSession,
            supplierUser: user?.role === 'Standard' || user?.role === 'Administrator',
            supplierAdmin: user?.role === 'Administrator',
            isValidToken,
            refreshToken,
            keepAlive,
            siteAccess: user?.siteAccess
        },
        isInitialised
    ];
};

export default authContext;
